import type { Variants } from 'motion';
import type { CopyProps } from '@/components/success-gambling/components/copy/copy.types';
import { motion, MotionConfig, AnimatePresence } from 'motion/react';
import clsx from 'clsx';
import { Copy as CopyIcon, CheckCheck } from 'lucide-react';
import { useToggle, useCopyToClipboard } from 'usehooks-ts';
import '@/components/success-gambling/components/copy/copy.css';

const CopyMotion = motion.create(CopyIcon);
const CheckCheckMotion = motion.create(CheckCheck);

const variants: Variants = {
  initial: { opacity: 0, scale: 0.9, x: 4 },
  animate: { opacity: 1, scale: 1, x: 0 },
  exit: { opacity: 0, scale: 0.9, x: -4 },
};

const DURATION = 0.125;
const DELAY = DURATION * 6000;

export default function Copy(props: CopyProps) {
  const { title = '', text = '', twoLines = false } = props;
  const [copied, toggleCopied] = useToggle(true);
  const [, copy] = useCopyToClipboard();

  const onClick = (): void => {
    copy(text)
      .then(() => toggleCopied())
      .catch((err) => console.error('Failed to copy text:', err))
      .finally(() => setTimeout(() => toggleCopied(), DELAY));
  };

  return (
    <div
      data-testid='copy-text'
      className={clsx('success-gambling-copy', { 'success-gambling-copy--two-lines': twoLines })}
    >
      {title && (
        <div data-testid='title' className='success-gambling-copy__title'>
          {title}
        </div>
      )}
      {text && (
        <div data-testid='text' className='success-gambling-copy__text'>
          {text}
        </div>
      )}
      <button
        data-testid='button'
        type='button'
        disabled={!copied}
        className='success-gambling-copy__button'
        onClick={onClick}
      >
        <MotionConfig transition={{ duration: DURATION, type: 'spring' }}>
          <AnimatePresence mode='wait'>
            {copied ? (
              <CopyMotion
                data-testid='copy-icon'
                key='copy'
                variants={variants}
                initial='initial'
                animate='animate'
                exit='exit'
                whileTap={{ scale: 1.125 }}
                className='success-gambling-copy__icon'
              />
            ) : (
              <CheckCheckMotion
                data-testid='check-icon'
                key='check'
                variants={variants}
                initial='initial'
                animate='animate'
                exit='exit'
                className='success-gambling-copy__icon'
              />
            )}
          </AnimatePresence>
        </MotionConfig>
      </button>
    </div>
  );
}
